import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Grid, Autocomplete } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormProps, getRequiredValidationRule, getValidationErrorProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { ApplicationQuestionOption } from 'store/types/ApplicationQuestion';
import ExplanationQuestionFormItem from '../ExplanationQuestionFormItem';

interface SelectQuestionFormItemProps extends FormProps {
  fieldName: string;
  options?: ApplicationQuestionOption[];
}

const SelectQuestionFormItem: React.FunctionComponent<SelectQuestionFormItemProps> = ({
  fieldName,
  options = [],
  required,
  disabled,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const [selectedValue, setSelectedValue] = useState<ApplicationQuestionOption | null>(null);
  const optionExplanation: ApplicationQuestionOption | undefined = useMemo(
    () =>
      selectedValue
        ? options.find(({ includeExplanation, value }) => !!includeExplanation && value === selectedValue.value)
        : undefined,
    [options, selectedValue]
  );

  useEffect(() => {
    const selectedOption = options.find(({ isSelected }) => isSelected);

    if (selectedOption) {
      const { value } = selectedOption;

      setValue(fieldName, value);
      setSelectedValue(selectedOption);
    }
  }, [options, setValue, fieldName, getValues]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              blurOnSelect
              autoHighlight
              value={selectedValue}
              disabled={disabled}
              options={options}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_event, data) => {
                setSelectedValue(data);
                field.onChange(data?.value);
              }}
              renderOption={(props, option) => (
                <li {...props} key={`${fieldName}-option-${option.value}`}>
                  {option.name ?? ''}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} {...getValidationErrorProps(error)} inputRef={field.ref} required={required} />
              )}
            />
          )}
          name={fieldName}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', true, required),
          }}
        />
      </Grid>
      {optionExplanation && (
        <Grid {...defaultGridItemProps}>
          <ExplanationQuestionFormItem
            parentFieldName={fieldName}
            required={required}
            defaultValue={optionExplanation.explanation}
            disabled={disabled}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default SelectQuestionFormItem;
